import React, { useState } from "react";
import { Typography, message } from "antd";
import { useLazyQuery } from "react-apollo";
import GET_HASHED_EMAIL_PARTITION_DOWNLOAD_URL from "../../../../core/GraphQl/Queries/GET_HASHED_EMAIL_PARTITION_DOWNLOAD_URL";

const { Link } = Typography;

/**
 * Renders one published HEM partition filename as a link that downloads
 * that CSV from S3 via a pre-signed URL.
 *
 * The URL is minted on click, not on mount — these URLs grant
 * unauthenticated read access to customer PII for 300 seconds, so eagerly
 * fetching one for every rendered filename (including audiences nobody
 * downloads from) would be wasteful and needlessly widen exposure. See
 * getHashedEmailPartitionDownloadUrl in mediajel-gql-service.
 */
const CsvFileNameLink = ({ hashedEmailListId, orgID, fileName }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const [fetchDownloadUrl] = useLazyQuery(
    GET_HASHED_EMAIL_PARTITION_DOWNLOAD_URL,
    {
      variables: { hashedEmailListId, orgID, fileName },
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        setIsDownloading(false);
        const result = data && data.getHashedEmailPartitionDownloadUrl;

        if (result && result.success !== false && result.downloadUrl) {
          // Content-disposition is set server-side, so this triggers a
          // download rather than navigating the tab away.
          const link = document.createElement("a");
          link.href = result.downloadUrl;
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }

        message.error(
          (result && result.error) ||
            `Couldn't download ${fileName}. Please try again.`
        );
      },
      onError: (error) => {
        setIsDownloading(false);
        message.error(
          `Couldn't download ${fileName}: ${error.message || "network error"}.`
        );
      },
    }
  );

  const handleClick = () => {
    // Guards against a double-click firing two requests while one is
    // already in flight.
    if (isDownloading) return;
    setIsDownloading(true);
    fetchDownloadUrl();
  };

  return (
    <Link onClick={handleClick} disabled={isDownloading}>
      {fileName}
      {isDownloading ? " (downloading…)" : ""}
    </Link>
  );
};

export default CsvFileNameLink;
